import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, CheckCircle2, ArrowRight, ArrowLeft,
  MessageSquare, Mail, Calendar, FileText, TrendingUp, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const tools = [
  {
    category: 'Writing & Content',
    description: 'Create better content faster',
    items: [
      { name: 'ChatGPT / Claude', use: 'Draft emails, blog posts, social media content, and marketing copy', tip: 'Always edit AI-generated content to match your brand voice. Use it as a starting point, not a final product.' },
      { name: 'Grammarly', use: 'Catch grammar errors and improve writing clarity', tip: 'The free version handles basics. Premium adds tone detection and style suggestions.' },
      { name: 'Jasper', use: 'Generate marketing-specific content with templates', tip: 'Best for teams doing high-volume content creation.' },
    ]
  },
  {
    category: 'Customer Service',
    description: 'Respond faster and more consistently',
    items: [
      { name: 'Intercom / Drift', use: 'AI chatbots that handle common questions 24/7', tip: 'Start with FAQ-based responses before enabling fully autonomous conversations.' },
      { name: 'Zendesk AI', use: 'Automatically categorize and route support tickets', tip: 'Works best when you have 6+ months of ticket history to train on.' },
      { name: 'Tidio', use: 'Affordable chatbot for small business websites', tip: 'Great entry point - free tier available with basic AI responses.' },
    ]
  },
  {
    category: 'Email & Marketing',
    description: 'Personalize at scale',
    items: [
      { name: 'Mailchimp AI', use: 'Optimize send times and generate subject lines', tip: 'A/B test AI suggestions against your own ideas to learn what works.' },
      { name: 'HubSpot AI', use: 'Score leads and personalize email sequences', tip: 'Requires consistent CRM data entry to be effective.' },
      { name: 'Phrasee', use: 'Generate and test email subject lines', tip: 'Particularly effective for e-commerce and promotional emails.' },
    ]
  },
  {
    category: 'Scheduling & Admin',
    description: 'Automate repetitive tasks',
    items: [
      { name: 'Calendly', use: 'Let clients self-schedule without back-and-forth', tip: 'Set buffer times between meetings to avoid burnout.' },
      { name: 'Otter.ai', use: 'Transcribe meetings and generate summaries', tip: 'Great for creating searchable archives of client calls.' },
      { name: 'Zapier AI', use: 'Create automated workflows between apps', tip: 'Start simple - automate one workflow completely before adding more.' },
    ]
  },
  {
    category: 'Data & Analytics',
    description: 'Make sense of your numbers',
    items: [
      { name: 'Google Analytics 4', use: 'AI-powered insights about website traffic patterns', tip: 'Check the "Insights" section regularly - it surfaces anomalies automatically.' },
      { name: 'MonkeyLearn', use: 'Analyze customer feedback and reviews at scale', tip: 'Useful when you have 100+ reviews to process.' },
      { name: 'Obviously AI', use: 'Predict outcomes without coding knowledge', tip: 'Best for businesses with clean, structured data in spreadsheets.' },
    ]
  },
];

const gettingStartedSteps = [
  { step: '1', title: 'Identify Your Biggest Time Sink', description: 'What task do you or your team spend the most time on that feels repetitive? That\'s your starting point.' },
  { step: '2', title: 'Start With One Tool', description: 'Don\'t try to implement five AI tools at once. Pick one, learn it well, then expand.' },
  { step: '3', title: 'Set Clear Success Metrics', description: 'Before implementing, define what success looks like. Hours saved? Faster response times? More leads?' },
  { step: '4', title: 'Train Your Team', description: 'AI tools are only as good as the people using them. Invest time in proper training.' },
  { step: '5', title: 'Review and Iterate', description: 'Check in monthly. Is the tool delivering value? Adjust or replace if not.' },
];

const GuideAIStarterPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="guide-ai-starter-page">
      <SEO 
        title="Small Business AI Starter Guide - Practical AI Tools"
        description="A practical introduction to AI tools that can actually help your small business save time and money. No hype, just actionable recommendations."
        keywords="AI for small business, AI tools for business, ChatGPT for business, AI automation, small business automation, AI marketing tools"
        url="/resources/guides/ai-starter"
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/resources/guides" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Guides
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full mb-6">
            <Zap className="h-4 w-4 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">AI & Automation</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-carbon-black mb-4" data-testid="guide-title">
            Small Business AI Starter Guide
          </h1>
          <p className="text-xl text-slate-gray leading-relaxed">
            A practical introduction to AI tools that can actually help your small business save time and money. No hype, just real tools solving real problems.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-slate-gray leading-relaxed">
              AI isn't just for tech giants anymore. Today's AI tools are accessible, affordable, and genuinely useful for small businesses. But with so much hype, it's hard to know what's actually worth your time.
            </p>
            <p className="text-slate-gray leading-relaxed">
              This guide cuts through the noise. We'll show you practical AI tools organized by business function, with honest assessments of what they're good for and tips for getting started.
            </p>
          </div>
          
          <div className="mt-8 p-6 bg-amber-50 rounded-xl border border-amber-100">
            <h3 className="font-bold text-carbon-black mb-2">A Word of Caution</h3>
            <p className="text-slate-gray text-sm">
              AI tools are powerful assistants, not replacements for human judgment. Always review AI-generated content before publishing, and never input sensitive customer data into free AI tools without understanding their privacy policies.
            </p>
          </div>
        </div>
      </section>

      {/* Tool Categories */}
      {tools.map((category, catIdx) => (
        <section key={catIdx} className={`py-12 sm:py-16 ${catIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-2">{category.category}</h2>
              <p className="text-slate-gray">{category.description}</p>
            </div>
            
            <div className="space-y-6">
              {category.items.map((tool, toolIdx) => (
                <div key={toolIdx} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold text-violet-600 mb-2">{tool.name}</h3>
                  <p className="text-carbon-black mb-3"><strong>What it does:</strong> {tool.use}</p>
                  <div className="flex items-start gap-2 p-3 bg-violet-50 rounded-lg">
                    <CheckCircle2 className="h-4 w-4 text-violet-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-gray"><strong className="text-carbon-black">Pro tip:</strong> {tool.tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Getting Started */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-violet-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-8 text-center">How to Get Started</h2>
          
          <div className="space-y-4">
            {gettingStartedSteps.map((step, idx) => (
              <div key={idx} className="flex items-start gap-4 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">{step.step}</span>
                </div>
                <div>
                  <h3 className="font-bold text-carbon-black mb-1">{step.title}</h3>
                  <p className="text-slate-gray">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Want Help Implementing AI in Your Business?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            We help businesses identify the right AI tools and integrate them into existing workflows. Let's talk about what would work for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/resources/consultation">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-violet-400 text-violet-600 hover:bg-violet-50 transition-all w-full sm:w-auto">
                More Guides
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default GuideAIStarterPage;
